import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {AnalyticsDefinition, VisionContext, VisionPolicy} from '../../models/vte';
import {AuthService} from '../auth.service';
import {DEFAULT_VISION_POLICY} from './vte.constants';
import {VteApiService} from './vte-api.service';

@Injectable({
    providedIn: 'root',
})
export class VisionConfigService {
    private readonly contextSubject = new BehaviorSubject<VisionContext | null>(null);
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    private loadedOrganizationId: number | null = null;

    readonly context$ = this.contextSubject.asObservable();
    readonly loading$ = this.loadingSubject.asObservable();

    constructor(
        private vteApi: VteApiService,
        private authService: AuthService,
    ) {
    }

    get snapshot(): VisionContext | null {
        return this.contextSubject.value;
    }

    get visionPolicy(): VisionPolicy {
        return this.contextSubject.value?.vision_policy ?? {...DEFAULT_VISION_POLICY};
    }

    get isVisionActive(): boolean {
        return this.visionPolicy.vision_mode !== 'disabled';
    }

    get supportedProviders() {
        return this.contextSubject.value?.supported_providers ?? [];
    }

    get enabledAnalytics(): AnalyticsDefinition[] {
        const context = this.contextSubject.value;
        if (!context) {
            return [];
        }
        // Vehicle Detection and enabled capabilities remain visible regardless of Vision Mode.
        // Vision Mode controls verification behaviour only (Phase 15).
        const capabilitySet = new Set(context.vision_policy.vision_capabilities || []);
        return (context.analytics_definitions || []).filter(
            (definition) =>
                definition.is_active
                && capabilitySet.has(definition.analytics_key),
        );
    }

    loadForCurrentOrganization(force = false): Observable<VisionContext> {
        const user = this.authService.getCurrentUserInfo();
        const orgId = user?.orgId;
        if (!orgId) {
            const fallback = this.buildFallbackContext(0);
            this.contextSubject.next(fallback);
            return of(fallback);
        }
        return this.loadForOrganization(Number(orgId), force);
    }

    loadForOrganization(organizationId: number, force = false): Observable<VisionContext> {
        if (!force && this.loadedOrganizationId === organizationId && this.contextSubject.value) {
            return of(this.contextSubject.value);
        }

        this.loadingSubject.next(true);
        return this.vteApi.getVisionContext(organizationId).pipe(
            tap((context) => {
                this.loadedOrganizationId = organizationId;
                this.contextSubject.next(this.normalizeContext(context, organizationId));
                this.loadingSubject.next(false);
            }),
            catchError(() => {
                const fallback = this.buildFallbackContext(organizationId);
                this.loadedOrganizationId = organizationId;
                this.contextSubject.next(fallback);
                this.loadingSubject.next(false);
                return of(fallback);
            }),
        );
    }

    shouldShowFact(outputFact: string): boolean {
        if (!outputFact?.trim()) {
            return false;
        }
        return this.enabledAnalytics.some(
            (definition) => definition.output_fact === outputFact,
        );
    }

    shouldShowAnalyticsKey(analyticsKey: string): boolean {
        if (!analyticsKey?.trim()) {
            return false;
        }
        return this.enabledAnalytics.some(
            (definition) => definition.analytics_key === analyticsKey,
        );
    }

    getDefinitionByOutputFact(outputFact: string): AnalyticsDefinition | undefined {
        return this.enabledAnalytics.find((definition) => definition.output_fact === outputFact);
    }

    getDefinitionByAnalyticsKey(analyticsKey: string): AnalyticsDefinition | undefined {
        return this.enabledAnalytics.find((definition) => definition.analytics_key === analyticsKey);
    }

    clear(): void {
        this.loadedOrganizationId = null;
        this.contextSubject.next(null);
    }

    private normalizeContext(context: VisionContext, organizationId: number): VisionContext {
        return {
            ...context,
            organization_id: context.organization_id ?? organizationId,
            vision_policy: {
                ...DEFAULT_VISION_POLICY,
                ...context.vision_policy,
                vision_capabilities: [...(context.vision_policy?.vision_capabilities || [])],
            },
            analytics_definitions: [...(context.analytics_definitions || [])],
            supported_providers: [...(context.supported_providers || [])],
        };
    }

    private buildFallbackContext(organizationId: number): VisionContext {
        return {
            organization_id: organizationId,
            vte_pipeline_enabled: false,
            vision_policy: {...DEFAULT_VISION_POLICY},
            analytics_definitions: [],
            supported_providers: [],
        };
    }
}
